import type { AssessmentSubmissionProgress } from "./ReadingAssessment/steps/types";

export type UnknownRecord = Record<string, unknown>;

export const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

export const asNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

export const findRawQuestionMetadata = (
  rawAssessment: unknown,
  questionId: string,
): UnknownRecord | undefined => {
  const normalizedQuestionId = questionId.trim();
  if (
    !normalizedQuestionId ||
    !rawAssessment ||
    typeof rawAssessment !== "object"
  ) {
    return undefined;
  }

  const source = rawAssessment as UnknownRecord;
  if (!source.part || typeof source.part !== "object") {
    return undefined;
  }

  const part = source.part as UnknownRecord;
  const questionCollections = [
    part.questions,
    part.yesNoQuestions,
    part.yes_no_questions,
    part.studentSelectQuestions,
    part.student_select_questions,
  ];

  for (const collection of questionCollections) {
    if (!Array.isArray(collection)) {
      continue;
    }

    const question = collection.find((item) => {
      if (!item || typeof item !== "object") {
        return false;
      }
      const candidate = item as UnknownRecord;
      return (
        asString(candidate.question_id || candidate.questionId) ===
        normalizedQuestionId
      );
    });

    if (question && typeof question === "object") {
      return question as UnknownRecord;
    }
  }

  return undefined;
};

export const normalizeSubmissionProgress = (
  raw: unknown,
): AssessmentSubmissionProgress | undefined => {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const source = raw as UnknownRecord;
  const rawAnswers =
    source.answers_by_question_id || source.answersByQuestionID || {};
  const answersByQuestionId =
    rawAnswers && typeof rawAnswers === "object"
      ? Object.fromEntries(
          Object.entries(rawAnswers as Record<string, unknown>)
            .filter(
              ([questionId, answer]) =>
                typeof questionId === "string" && typeof answer === "string",
            )
            .map(([questionId, answer]) => [questionId, answer as string]),
        )
      : undefined;

  const rawCompletedStepKeys = Array.isArray(source.completed_step_keys)
    ? source.completed_step_keys
    : Array.isArray(source.completedStepKeys)
      ? source.completedStepKeys
      : [];

  return {
    submission_id: asString(source.submission_id || source.submissionId),
    status: asString(source.status),
    last_seen_step_key: asString(
      source.last_seen_step_key || source.lastSeenStepKey,
    ),
    completed_step_keys: rawCompletedStepKeys.filter(
      (value): value is string => typeof value === "string",
    ),
    answers_by_question_id: answersByQuestionId,
  };
};
